import { useContext } from 'react';
import { useMutation } from '@apollo/client';
import { formatPresentSlides } from '@/components/admin/SlideShow/PresentMode/Utils';
import { SlideShowContext } from '@/components/admin/SlideShow/stores/SlideShowStoreProvider';
import { REORDER_SLIDE } from '@/components/admin/SlideShow/Utils/slideShowGraphQL';
import { DisplayItem, getSlideReorderParams } from './slideReorder';

interface UseSlideReorderOptions {
  setIsSaving: (saving: boolean) => void;
}

interface MinimalSlide {
  id: string;
  position: number;
  slideGroupId: number | null;
}

// Persists a slide drag: optimistic display update, REORDER_SLIDE mutation, then merge the server's positions
// into the full slide records (which carry slideObjects the response does not).
const useSlideReorder = ({ setIsSaving }: UseSlideReorderOptions) => {
  const [updateSlideMutation] = useMutation(REORDER_SLIDE);
  const store = useContext(SlideShowContext);

  return (slideId: string, destinationPosition: number) => {
    const { setSlides, slideShow, slides: displayItems, updateSlideShow } = store.getState();
    const params = getSlideReorderParams({
      destinationPosition,
      displayItems: displayItems as DisplayItem[],
      slideId,
      slides: slideShow.slides,
    });
    if (!params) return;

    setSlides(formatPresentSlides({ ...slideShow, slides: params.slides }, params.slides.map(slide => slide.id)));
    setIsSaving(true);

    updateSlideMutation({
      variables: { id: slideId, position: params.dbPosition, slideGroupId: params.slideGroupId },
    })
      .then((res) => {
        const { slideGroups, slides: minimalSlides } = res.data.updateSlide.slideShow;
        const updatesById = (minimalSlides as MinimalSlide[]).reduce((acc, slide) => {
          acc[slide.id] = { position: slide.position, slideGroupId: slide.slideGroupId };

          return acc;
        }, {} as Record<string, Pick<MinimalSlide, 'position' | 'slideGroupId'>>);

        const slides = store.getState().slideShow.slides
          .map(slide => ({ ...slide, ...updatesById[String(slide.id)] }))
          .sort((a, b) => a.position - b.position);

        updateSlideShow({ slideGroups, slides });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error);
        updateSlideShow(slideShow);
      })
      .finally(() => setIsSaving(false));
  };
};

export default useSlideReorder;
